import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  OutlinedInput,
  ListItemText,
  Chip
} from '@mui/material';
import * as S from './style';

// ============================================================
// Web-to-Lead configuration per Salesforce environment
// To switch environments, set VITE_SF_ENVIRONMENT in client/.env
// ============================================================

type EnvConfig = {
  url: string;
  oid: string;
  recordType: string;
  fields: {
    organisation: string;
    organisationSize: string;
    sector: string;
    otherSector: string;
    planningToEnter: string;
    motivation: string;
    howDidYouHear: string;
    receiveAllComms: string;
    receiveIndexComms: string;
  };
  howDidYouHearOptions: string[];
};

const SF_ENVIRONMENTS: Record<string, EnvConfig> = {
  partial: {
    url: 'https://test.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=00DE100000LDDEf',
    oid: '00DE100000LDDEf',
    recordType: '012TV000001Mp89',
    fields: {
      organisation: '00NC0000005OXvR',
      organisationSize: '00NTV000000zzJX',
      sector: '00NC0000005OMRQ',
      otherSector: '00NC0000005PDcr',
      planningToEnter: '00NTV000000zzJW',
      motivation: '00NTV000000zzJZ',
      howDidYouHear: '00NC0000005OrBw',
      receiveAllComms: '00NTV000001P7vN',
      receiveIndexComms: '00NTV000001P7vO',
    },
    howDidYouHearOptions: [
      'Another organisation',
      'An internet search engine',
      'Colleague',
      'Existing mentor',
      'Facebook',
      'Friend',
      'LinkedIn',
      'Instagram',
      'One +1 Campaign',
      'Professional body',
      'SMF Alumni Newsletter',
      'STEM Ambassador Hub',
      'SMF Video',
      'SMF Website',
      'Television/Radio',
      'Twitter',
      'Workplace scheme',
      'Other',
    ],
  },
  staging: {
    url: 'https://test.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=00DU8000005lXZ0',
    oid: '00DU8000005lXZ0',
    recordType: '012TV000001Mp89',
    fields: {
      organisation: '00NC0000005OXvR',
      organisationSize: '00NTV000000zzJX',
      sector: '00NC0000005OMRQ',
      otherSector: '00NC0000005PDcr',
      planningToEnter: '00NTV000000zzJW',
      motivation: '00NTV000000zzJZ',
      howDidYouHear: '00NTV000000zzJV',
      receiveAllComms: '00NTV000001P7vN',
      receiveIndexComms: '00NTV000001P7vO',
    },
    howDidYouHearOptions: [
      'Through your organisation\'s work with the Social Mobility Foundation on one of our student programmes',
      'Through direct contact from a Social Mobility Foundation employee (not related to your organisation\'s work on one of our programmes)',
      'Through your organisation\'s engagement with the Department for Opportunities advocacy and campaigning work',
      'Via the Social Mobility Foundation\'s social media',
      'From another employer who had previously submitted to the Index',
      'Through a trade association',
      'Through a professional body',
      'Through a diversity & inclusion consultancy',
      'Through a regulatory body',
      'At a webinar or event',
      'Your organisation\'s own research',
      'Don\'t know',
    ],
  },
};

const currentEnv = import.meta.env.VITE_SF_ENVIRONMENT || 'partial';
const config = SF_ENVIRONMENTS[currentEnv] || SF_ENVIRONMENTS.partial;

const orgSizeOptions = [
  'Less than 50 employees',
  '50-250 employees',
  '250+ employees',
];

const sectorOptions = [
  'Accountancy',
  'Architecture',
  'Banking &amp; Finance',
  'Biology &amp; Chemistry',
  'Business',
  'Digital',
  'Engineering &amp; Physics',
  'Law',
  'Media &amp; Communciations',
  'Medicine',
  'Politics',
  'Other',
];

const planningOptions = ['Yes', 'No', 'Not sure'];

const motivationOptions = [
  'To receive feedback to improve our organisation',
  'To help meet our diversity goals',
  'To receive public ranking to include in our publicity',
  'To use our public ranking to win business or contracts',
];

const RegisterDev: React.FC = () => {
  const [sector, setSector] = useState('');
  const [motivation, setMotivation] = useState<string[]>([]);
  const [receiveIndexComms, setReceiveIndexComms] = useState(false);
  const [receiveAllComms, setReceiveAllComms] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [message, setMessage] = useState('');

  const submitted = new URLSearchParams(window.location.search).get('submitted') === 'true';

  const { fields } = config;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name: string, value: any) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Build a hidden form and submit it natively (Salesforce Web-to-Lead doesn't support CORS)
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = config.url;

    const addField = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    addField('oid', config.oid);
    // addField('debug', '1');
    // addField('debugEmail', 'ahmed+smf-index@yallacooperative.com');
    addField('retURL', window.location.origin + '/register-dev?submitted=true');
    addField('recordType', config.recordType);
    addField('first_name', formData.first_name || '');
    addField('last_name', formData.last_name || '');
    addField('email', formData.email || '');
    addField('title', formData.title || '');
    addField(fields.organisation, formData[fields.organisation] || '');
    addField(fields.organisationSize, formData[fields.organisationSize] || '');
    addField(fields.sector, sector);
    if (sector === 'Other') addField(fields.otherSector, formData[fields.otherSector] || '');
    addField(fields.planningToEnter, formData[fields.planningToEnter] || '');
    motivation.forEach(m => addField(fields.motivation, m));
    addField(fields.howDidYouHear, formData[fields.howDidYouHear] || '');

    // check
    addField('company', formData[fields.organisation] || '');

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <S.Container>
      <S.Header>Register to get started</S.Header>
      <S.Subheader>Find out more about the Employer Index and how to enter.</S.Subheader>
      <Box mb={1} fontSize={12} color="gray">Environment: {currentEnv}</Box>
      {submitted && <Box color="green" mb={2}>Thank you for registering! Check your email for the next steps.</Box>}
      {message && <Box color="red" mb={2}>{message}</Box>}
      <S.Form onSubmit={handleSubmit}>
        <S.Row>
          <S.StyledTextField name="first_name" label="First Name" variant="outlined" fullWidth inputProps={{ maxLength: 40 }} value={formData.first_name || ''} onChange={handleChange} />
          <S.StyledTextField name="last_name" label="Last Name" variant="outlined" fullWidth inputProps={{ maxLength: 80 }} value={formData.last_name || ''} onChange={handleChange} />
        </S.Row>
        <S.Row>
          <S.StyledTextField name="title" label="Job Title" variant="outlined" fullWidth inputProps={{ maxLength: 40 }} value={formData.title || ''} onChange={handleChange} />
          <S.StyledTextField name="email" label="Email Address" variant="outlined" fullWidth inputProps={{ maxLength: 80 }} value={formData.email || ''} onChange={handleChange} />
        </S.Row>
        <S.StyledTextField name={fields.organisation} label="Organisation" variant="outlined" fullWidth inputProps={{ maxLength: 100 }} value={formData[fields.organisation] || ''} onChange={handleChange} />
        <FormControl fullWidth>
          <InputLabel>What is the approx. number of employees/size of your organisation?</InputLabel>
          <Select name={fields.organisationSize} label="What is the approx. number of employees/size of your organisation?" value={formData[fields.organisationSize] || ''} onChange={e => handleSelectChange(fields.organisationSize, e.target.value)}>
            <MenuItem value="">Select one...</MenuItem>
            {orgSizeOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Please select the sector your organisation sits within:</InputLabel>
          <Select
            name={fields.sector}
            label="Please select the sector your organisation sits within:"
            value={sector}
            onChange={e => setSector(e.target.value)}
          >
            <MenuItem value="">Select one...</MenuItem>
            {sectorOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </Select>
        </FormControl>
        {sector === 'Other' && (
          <S.StyledTextField
            name={fields.otherSector}
            label="If you selected 'Other', please describe the sector:"
            variant="outlined" fullWidth inputProps={{ maxLength: 50 }}
            value={formData[fields.otherSector] || ''}
            onChange={handleChange}
          />
        )}
        <FormControl fullWidth>
          <InputLabel>Are you planning to enter in 2026?</InputLabel>
          <Select name={fields.planningToEnter} label="Are you planning to enter in 2025?" value={formData[fields.planningToEnter] || ''} onChange={e => handleSelectChange(fields.planningToEnter, e.target.value)}>
            <MenuItem value="">Select one...</MenuItem>
            {planningOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>What is your motivation for entering the Index/finding out more about the Index?</InputLabel>
          <Select
            name={fields.motivation}
            multiple
            value={motivation}
            onChange={e => setMotivation(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label="What is your motivation for entering the Index/finding out more about the Index?" />}
            renderValue={selected => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map(value => <Chip key={value} label={value} />)}
              </Box>
            )}
          >
            {motivationOptions.map(opt => (
              <MenuItem key={opt} value={opt}>
                <Checkbox checked={motivation.indexOf(opt) > -1} />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>How did you hear about us?</InputLabel>
          <Select name={fields.howDidYouHear} label="How did you hear about us?" value={formData[fields.howDidYouHear] || ''} onChange={e => handleSelectChange(fields.howDidYouHear, e.target.value)}>
            <MenuItem value="">Select one...</MenuItem>
            {config.howDidYouHearOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </Select>
        </FormControl>
        <S.CheckboxRow>
          <FormControlLabel
            name={fields.receiveIndexComms}
            control={<Checkbox checked={receiveIndexComms} onChange={e => setReceiveIndexComms(e.target.checked)} name="indexComms" />}
            label="Receiving index communications from SMF"
          />
          <FormControlLabel
            name={fields.receiveAllComms}
            control={<Checkbox checked={receiveAllComms} onChange={e => setReceiveAllComms(e.target.checked)} name="allComms" />}
            label="Receiving all communications from SMF"
          />
        </S.CheckboxRow>
        <S.SubmitBtn type="submit" variant="contained">Register your interest</S.SubmitBtn>
      </S.Form>
    </S.Container>
  );
};

export default RegisterDev;
